import { useCallback } from "react";

import { CloudflowExecutionStatus } from "@doitintl/cmp-models";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider } from "@mui/material";
import DialogContentText from "@mui/material/DialogContentText";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { cloudflowTexts, globalText } from "../../../assets/texts";
import { CircularProgressLoader } from "../../../Components/Loader";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useDeleteCloudflow, useHasActiveCloudflowExecutions } from "../hooks";

const CantDeleteCloudflowAlert = ({ handleClose }: { handleClose: () => void }) => (
  <>
    <DialogTitle>{cloudflowTexts.CANT_DELETE_CLOUDFLOW_DIALOG_TITLE}</DialogTitle>
    <DialogContent>
      <DialogContentText>{cloudflowTexts.CANT_DELETE_CLOUDFLOW_DIALOG_TEXT}</DialogContentText>
    </DialogContent>
    <Divider />
    <DialogActions>
      <Button onClick={handleClose} variant="contained" color="primary">
        {globalText.DONE}
      </Button>
    </DialogActions>
  </>
);

type DeleteCloudFlowDialogProps = {
  flowId: string | null;
  handleClose: () => void;
};

const DeleteCloudFlowDialog = ({ flowId, handleClose }: DeleteCloudFlowDialogProps) => {
  const { customer } = useCustomerContext();
  const { breakpoints } = useTheme();
  const fullScreen = useMediaQuery(breakpoints.down("md"));
  const { hasActiveExecutions, executions, loading } = useHasActiveCloudflowExecutions(flowId ?? "");
  const [deleteCloudflow, deleteCloudflowLoading] = useDeleteCloudflow();

  const isAwaitingApproval = executions?.some((e) => e.status === CloudflowExecutionStatus.PENDING_APPROVAL);

  const deleteCloudFlow = useCallback(
    async (id: string) => {
      await deleteCloudflow(customer.id, { id });
    },
    [customer.id, deleteCloudflow]
  );

  const handleDelete = useCallback(async () => {
    if (!flowId) {
      return;
    }

    await deleteCloudFlow(flowId);
    handleClose();
  }, [deleteCloudFlow, handleClose, flowId]);

  // NOTE: flowId addition here stops flicker of loading when closing the dialog
  if (flowId && loading)
    return (
      <Dialog open={true} onClose={handleClose} fullScreen={fullScreen} fullWidth>
        <DialogContent sx={{ minHeight: 150 }}>
          <CircularProgressLoader />
        </DialogContent>
      </Dialog>
    );

  if (!loading && hasActiveExecutions && !isAwaitingApproval)
    return (
      <Dialog open={true} onClose={handleClose} fullScreen={fullScreen} fullWidth>
        <CantDeleteCloudflowAlert handleClose={handleClose} />
      </Dialog>
    );

  return (
    <Dialog open={true} onClose={handleClose} fullScreen={fullScreen} fullWidth>
      <DialogTitle>{cloudflowTexts.DELETE_CLOUDFLOW}</DialogTitle>
      <DialogContent>
        <DialogContentText>{cloudflowTexts.CLOUDFLOW_DELETE_DIALOG_TEXT}</DialogContentText>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={handleClose} variant="text" color="primary">
          {cloudflowTexts.CANCEL}
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          loading={deleteCloudflowLoading}
          disabled={deleteCloudflowLoading}
          data-cy="delete-cloudflow-button"
        >
          {cloudflowTexts.DELETE_CLOUDFLOW}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteCloudFlowDialog;
